import _ from 'lodash';
import { addAssetOrItem, setIsLoading } from '@/assetGroupEditor/assetGroup.actions';
import { CREATED_BY_SEEQ_WORKBENCH } from '@/main/app.constants';
import { IconWithManagedSpinner } from '@/core/IconWithManagedSpinner.atom';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { isAsset, isTableDefinition } from '@/utilities/utilities';
import {
  sqAssetGroupStore,
  sqItemTableDefinitionStore,
  sqLayoutStore,
  sqLicenseManagementStore,
  sqScalingTableDefinitionStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { editAssetGroup, validateItemToAdd } from '@/assetGroupEditor/assetGroup.utilities';
import { canWriteItem } from '@/services/authorization.service';
import { setDisplayAssetGroupEditor } from '@/worksheet/worksheet.actions';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setAutoFocusItemTableTab, setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import type { SelectedSearchItem } from '@/search/search.types';

interface SearchResultAssetIconProps {
  item: SelectedSearchItem & { isAssetGroupItem?: boolean };
  isAssetGroupView: boolean;
}

export const SearchResultAssetIcons: React.FunctionComponent<SearchResultAssetIconProps> = ({
  item,
  isAssetGroupView,
}) => {
  const storeAssets = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.assets);
  const isTableDefinitionView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayTableDefinitionView);
  const isItemTableView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayItemTableView);
  const isEditingAssetGroup = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.id === item.id);
  const scalingTableEditorVisible = useFluxPath(sqLayoutStore, () => sqLayoutStore.tableDefinitionEditorVisible);
  const itemTableEditorVisible = useFluxPath(sqLayoutStore, () => sqLayoutStore.itemTableEditorVisible);
  const isEditingTableDefinition =
    useFluxPath(sqScalingTableDefinitionStore, () => sqScalingTableDefinitionStore.id === item.id) &&
    isTableDefinitionView;
  const isEditingItemTableDefinition =
    useFluxPath(sqItemTableDefinitionStore, () => sqItemTableDefinitionStore.id === item.id) && isItemTableView;
  const isEditTableDefinitionAllowed = isTableDefinition(item);
  const isEditAllowed =
    isEditTableDefinitionAllowed ||
    (isAsset(item) &&
      sqLicenseManagementStore.hasAssetGroups() &&
      canWriteItem(item) &&
      (_.find(item.properties, { name: SeeqNames.Properties.TreeType })?.value === CREATED_BY_SEEQ_WORKBENCH ||
        item.isAssetGroupItem) &&
      _.isEmpty(item.ancestors));

  const disableEdit =
    isEditingAssetGroup ||
    (isEditingTableDefinition && scalingTableEditorVisible) ||
    (isEditingItemTableDefinition && itemTableEditorVisible);

  const addAsset = (addMatchingItemsOnly = false, addChildrenOfItemsOfItems = false) => {
    setIsLoading(true);
    const promise = validateItemToAdd({ item: { type: item.type || '' } })
      ? addAssetOrItem({
          assetOrItem: item as SelectedSearchItem & {
            ancestors?: Array<{ type?: string; name?: string; id?: string }>;
          },
          addMatchingItemsOnly,
          addItemsOfItemsChildren: addChildrenOfItemsOfItems,
        })
      : Promise.resolve();

    return promise.finally(() => setIsLoading(false));
  };

  const isScalingTable = (
    item: SelectedSearchItem & { includedProperties?: Record<string, { value?: string }> },
  ): boolean => {
    return item.includedProperties?.[SeeqNames.Properties.TableType]?.value?.includes('SCALING') ?? false;
  };

  const isItemTable = (
    item: SelectedSearchItem & { includedProperties?: Record<string, { value?: string }> },
  ): boolean => {
    return item.includedProperties?.[SeeqNames.Properties.TableType]?.value?.includes('ITEM') ?? false;
  };

  const loadTableDefinitionForEdit = (): Promise<void> => {
    if (isItemTable(item)) {
      setAutoFocusItemTableTab(true);
      return editTableDefinition(item, 'ITEM');
    } else if (isScalingTable(item)) {
      setAutoFocusTableDefinitionTab(true);
      return editTableDefinition(item, 'SCALING');
    }
    return Promise.resolve();
  };

  const loadAssetGroupForEdit = (): Promise<void> => {
    setDisplayAssetGroupEditor(true);
    return editAssetGroup(item);
  };

  let tableDefinitionEditTooltip: string | undefined;
  if (isEditTableDefinitionAllowed) {
    if (!isEditingTableDefinition && isScalingTable(item)) {
      tableDefinitionEditTooltip = 'SEARCH_DATA.EDIT_SCALING_TABLE_TOOLTIP';
    } else if (!isEditingItemTableDefinition && isItemTable(item)) {
      tableDefinitionEditTooltip = 'SEARCH_DATA.EDIT_ITEM_TABLE_TOOLTIP';
    }
  }
  const tooltip =
    !isEditingAssetGroup && isAsset(item) ? 'SEARCH_DATA.EDIT_ASSET_GROUP_TOOLTIP' : tableDefinitionEditTooltip;
  let testId = '';
  if (isAsset(item)) {
    testId = 'editAssetGroupIcon';
  } else if (isEditTableDefinitionAllowed && isScalingTable(item)) {
    testId = 'editScalingTableIcon';
  } else if (isEditTableDefinitionAllowed && isItemTable(item)) {
    testId = 'editItemTableDefinitionIcon';
  }
  return (
    <>
      {isEditAllowed && (
        <IconWithManagedSpinner
          icon="fa-pencil"
          testId={testId}
          disabled={disableEdit}
          large={true}
          correctSpin={false}
          extraClassNames="searchBtn"
          action={isAsset(item) ? loadAssetGroupForEdit : loadTableDefinitionForEdit}
          actionOnEnter={true}
          tooltip={tooltip || ''}
          tooltipDelay={500}
        />
      )}

      {isAssetGroupView && (
        <>
          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && (
            <IconWithManagedSpinner
              icon="fc-plus-assets"
              extraClassNames="searchBtn"
              action={() => addAsset(false, !isAsset(item))}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIcon"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && !_.isEmpty(storeAssets) && (
            <IconWithManagedSpinner
              icon="fc-plus-selected"
              extraClassNames="searchBtn"
              action={() => addAsset(true)}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIconSameChildrenOnly"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_CHILDREN_MATCH_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* items (including items of items' parents) */}
          {!isAsset(item) && !isTableDefinition(item) && (
            <IconWithManagedSpinner
              icon="fa-plus-circle"
              extraClassNames="searchBtn"
              action={addAsset}
              actionOnEnter={true}
              correctSpin={false}
              large={true}
              testId="addItemToAssetIcon"
              tooltip="SEARCH_DATA.ADD_ASSET_ADD_ITEM_TOOLTIP"
              tooltipDelay={500}
            />
          )}
        </>
      )}
    </>
  );
};
